// Mapeo contable del módulo Control Bancario. Configuración → Cuentas contables.
// Define la cuenta de Bancos por cada cuenta bancaria y las contrapartidas por
// concepto (cb_*). La contabilización por movimiento (solo conciliados) usa este
// mapeo; sin él, el movimiento queda "conciliado sin contabilizar".
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Calculator, CheckCircle2, Landmark, Loader2, PlayCircle, Save, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { BuscadorCuenta } from '@/components/ui/BuscadorCuenta';
import { getErrorMessage } from '@/lib/api/client';
import { Cuenta, MapeoContable as TMapeo, PlanCuenta, cb, cbRpc } from './shared';

export function MapeoContableCB() {
  const qc = useQueryClient();
  const { data: plan = [] } = useQuery<PlanCuenta[]>({ queryKey: ['cb-plan-cuentas'], queryFn: () => cbRpc<PlanCuenta[]>('cb_plan_cuentas') });
  const { data: mapeo = [], isFetching } = useQuery<TMapeo[]>({ queryKey: ['cb-mapeo'], queryFn: () => cbRpc<TMapeo[]>('cb_mapeo_contable_get') });
  const { data: cuentas = [] } = useQuery<(Cuenta & { banco?: { nombre: string } })[]>({
    queryKey: ['cb-cuentas'],
    queryFn: () => cb.list('cuentas', '?select=*,banco:banco_id(nombre)&deleted_at=is.null&order=alias,numero'),
  });

  const [edit, setEdit] = useState<Record<string, string>>({});
  useEffect(() => { setEdit(Object.fromEntries(mapeo.map((m) => [m.concepto, m.cuenta_id ?? '']))); }, [mapeo]);

  const guardar = useMutation({
    mutationFn: async () => {
      for (const m of mapeo) {
        const nuevo = edit[m.concepto] || null;
        if ((m.cuenta_id ?? '') !== (nuevo ?? '')) await cbRpc('cb_mapeo_contable_set', { _concepto: m.concepto, _cuenta_id: nuevo });
      }
    },
    onSuccess: () => { toast.success('Mapeo guardado'); void qc.invalidateQueries({ queryKey: ['cb-mapeo'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const contabilizar = useMutation({
    mutationFn: () => cbRpc<{ contabilizados: number; sin_mapeo: number }>('cb_contabilizar_pendientes', { _cuenta_id: null, _hasta: null }),
    onSuccess: (r) => { toast.success(`Contabilizados ${r.contabilizados} · sin mapeo ${r.sin_mapeo}`); void qc.invalidateQueries({ queryKey: ['cb-libro'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const planTxt = (id: string | null) => { const p = plan.find((x) => x.id === id); return p ? `${p.codigo} · ${p.nombre}` : null; };
  const bancosSinMapear = cuentas.filter((c) => !c.cuenta_contable_id).length;

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Calculator className="w-5 h-5" /> Cuentas contables del módulo</h1>
        <Button variant="outline" onClick={() => contabilizar.mutate()} isLoading={contabilizar.isPending}><PlayCircle className="w-4 h-4 mr-1" /> Contabilizar conciliados pendientes</Button>
      </div>
      <p className="text-sm text-muted-foreground">La contabilización es <b>por movimiento y solo al conciliar</b>. Cada asiento usa la cuenta de Bancos de la cuenta bancaria y una contrapartida según el tipo de movimiento. Lo que no esté mapeado queda “conciliado sin contabilizar”.</p>

      {/* Cuenta de Bancos por cuenta bancaria */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Landmark className="w-4 h-4" /> Cuenta de Bancos por cuenta bancaria</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <p className="pb-2 text-xs text-muted-foreground">Se define en cada cuenta (Bancos y Cuentas → Cuentas). {bancosSinMapear > 0 && <span className="text-pendiente">{bancosSinMapear} cuenta(s) sin mapear.</span>}</p>
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Cuenta</th><th className="py-2 pr-3">Banco</th><th className="py-2 pr-3">Cuenta contable</th></tr></thead>
            <tbody>
              {cuentas.length === 0 && <tr><td colSpan={3} className="py-6 text-center text-muted-foreground">Sin cuentas</td></tr>}
              {cuentas.map((c) => (
                <tr key={c.id} className="border-b last:border-0">
                  <td className="py-2 pr-3">{c.alias ?? c.numero}</td>
                  <td className="py-2 pr-3">{c.banco?.nombre}</td>
                  <td className="py-2 pr-3">{c.cuenta_contable_id ? <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4 text-estable" /> {planTxt(c.cuenta_contable_id) ?? 'mapeada'}</span> : <span className="inline-flex items-center gap-1 text-pendiente"><XCircle className="w-4 h-4" /> sin mapear</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Contrapartidas por concepto */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Contrapartidas por concepto</CardTitle>{isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary" />}</CardHeader>
        <CardContent className="space-y-2">
          {mapeo.map((m) => (
            <div key={m.concepto} className="flex items-center gap-3 border-b last:border-0 py-2">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">{m.label}</div>
                <div className="text-xs text-muted-foreground font-mono">{m.concepto}</div>
              </div>
              <div className="max-w-xs w-full">
                <BuscadorCuenta value={edit[m.concepto] ?? ''} onChange={(id) => setEdit((s) => ({ ...s, [m.concepto]: id }))} allowClear compact />
              </div>
            </div>
          ))}
          <div className="flex justify-end pt-2">
            <Button isLoading={guardar.isPending} onClick={() => guardar.mutate()}><Save className="w-4 h-4 mr-1" /> Guardar mapeo</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
